import axios from 'axios'
import type { AxiosError } from 'axios'

/**
 * Cliente HTTP da aplicação.
 *
 * A autenticação é por COOKIE de sessão (Sanctum SPA), não por token: não existe
 * nada guardado em localStorage aqui, de propósito. Um XSS nesta aplicação não
 * consegue ler o cookie de sessão, porque ele é HttpOnly.
 *
 * O preço disso é o CSRF, que o Laravel trata exigindo o header X-XSRF-TOKEN.
 */
const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',

  // Envia o cookie de sessão em toda requisição.
  withCredentials: true,

  // A partir do axios 1.6 isto virou opt-in explícito: sem ele o axios lê o cookie
  // XSRF-TOKEN mas NÃO manda o header, e todo POST volta 419 sem explicação.
  withXSRFToken: true,

  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/**
 * Busca o cookie CSRF antes da primeira requisição que altera estado.
 *
 * A rota /sanctum/csrf-cookie fica fora do prefixo /api, então usa o axios global
 * com caminho absoluto — e não este cliente.
 */
export async function ensureCsrfCookie(): Promise<void> {
  await axios.get('/sanctum/csrf-cookie', { withCredentials: true })
}

/** Erro de validação do Laravel (HTTP 422). */
export interface ValidationErrorBody {
  message: string
  errors: Record<string, string[]>
}

export function isValidationError(
  error: unknown,
): error is AxiosError<ValidationErrorBody> {
  return axios.isAxiosError(error) && error.response?.status === 422
}

/** Reexportado para as telas distinguirem 403 de resposta vazia sem importar o axios. */
export function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error)
}

export function isUnauthenticated(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401
}

export default client
